<template>
  <div id="root">
    <modal
      name="tiptap-modal"
      height="auto"
      width="700px"
      :click-to-close="false"
      :adaptive="true"
      :scrollable="true"
      draggable=".custom-text-editor > .drag-point"
      @before-open="beforeOpenTipTapModal"
    >
      <tiptap
        :id="tipTapContentShapeId"
        :attributes="tipTapAttributes"
        @update:attributes="updateCustomEditorContentAttributes"
        @text-editor:close="$modal.hide('tiptap-modal')"
        @wizard:price-params-changed="
          (event) => $emit('wizard:price-params-changed', event)
        "
      />
    </modal>
    <slot
      name="toolbar"
      v-bind="{
        tools: customTools,
        activeInteractionMode,
      }"
    ></slot>
    <slot name="header" />
    <crop-editor
      v-if="initializeCropEditor"
      @close:crop-editor="() => (initializeCropEditor = false)"
    />

    <lanyard-panel v-if="isLanyardPanelOpen" />
    <round-item-panel
      v-if="isRoundItemPanelOpen"
      :is-disabled="isRoundItemDisabled()"
      @position-logo="positionLogoInFirstPanel"
      @duplicate-logo="executeDuplicateToOposite"
    />
    <!-- eslint-disable -->
    <PinturaEditor
      ref="pinturaEditor"
      v-bind="editorDefaults"
      :src="src"
      :util="util"
      :disabled="isDisabled"
      :imageAnnotation="imageAnnotation"
      :imageDecoration="imageDecoration"
      :beforeUpdateShape="beforeUpdateShape"
      :beforeSelectShape="beforeSelectShape"
      :beforeDeselectShape="beforeDeselectShape"
      :handleEvent="handleEvent"
      :beforeRemoveShape="beforeRemoveShape"
      :previewUpscale="true"
      :markupEditorZoomLevels="[
        0.1, 0.15, 0.2, 0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3,
      ]"
      :markupEditorZoomAdjustStep="0.01"
      :markupEditorZoomAdjustFactor="0.1"
      @pintura:load="(e) => onLoad(e)"
      @pintura:ready="(e) => $emit('ready', e)"
      @pintura:loaderror="(e) => $emit('load-error', e)"
      @pintura:updateshape="(e) => $emit('update-shape', e)"
      @pintura:removeshape="(e) => $emit('remove-shape', e, true)"
      @pintura:addshape="(e) => $emit('add-shape', e.id)"
      @pintura:selectshape="(e) => $emit('select-shape', e.id)"
      @pintura:zoom="(e) => $emit('action:zoom', e)"
      @pintura:undo="undoHandler"
      @pintura:redo="redoHandler"
    />
    <!-- eslint-enable -->
  </div>
</template>
<script>
/* eslint-disable @typescript-eslint/no-unused-vars */
import Vue from 'vue';
import { v4 as uuidv4 } from 'uuid';
import cloneDeep from 'lodash.clonedeep';
import {
  /**
   * Import the default image reader and writer
   */
  createDefaultImageReader,
  createDefaultImageWriter,

  /**
   * The method used to register the plugins
   */
  setPlugins,

  /**
   * The plugins we want to use
   */
  plugin_decorate,
  plugin_crop,
  plugin_annotate,

  /**
   * The user interface and plugin locale objects
   */
  locale_en_gb,
  plugin_decorate_locale_en_gb,

  /**
   * Because we use the annotate plugin we also need to import the markup editor
   * locale
   */
  markup_editor_locale_en_gb,

  /**
   * Import the default configuration for the markup editor and finetune
   * plugins markup_editor_defaults,
   */
  createNode,
  findNode,
  appendNode,
} from 'pintura';

import { PinturaEditor } from './PinturaEditor/index';
import { Tiptap } from '@/components/Tiptap';

import { mapState, mapActions, mapStores } from 'pinia';
import { useStore as useProductStore } from '@/stores/product';
import { useStore as useShapeStore } from '@/stores/shape';
import { useStore as useHistoryStore } from '@/stores/history';
import { useStore as useMirrorStore } from '@/stores/mirror';
import { useStore as useNotificationStore } from '@/stores/notification';

import {
  isPercentageValue,
  limitToContextWithCorrectionFactor,
  mm2px,
  px2mm,
  recalculateDPI,
} from '@/utils/common';

import {
  alignBottomControl,
  alignLeftControl,
  alignRightControl,
  alignTopControl,
} from '@/utils/alignLogo';

import { createCustomTools } from './PinturaControls/toolControls.ts';
import { nodes as contextNodes } from './PinturaControls/contextControls.ts';
import CropEditor from '../CropEditor/CropEditor.vue';
import { lineWidth } from '@/constants/areaLineWidth';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { createStateSnapshot } from '@/utils/historyHelper';
import { LanyardPanel } from '../w2pPintura/Panels';
import { RoundItemPanel } from '../w2pPintura/Panels';
import { Category } from '@midocean/webapps-shared-domain';
import { generateEventPayload } from '@/utils/generatePriceParamsEventPayload';

setPlugins(plugin_decorate, plugin_crop, plugin_annotate);

export default Vue.extend({
  name: 'W2pPintura',
  components: {
    PinturaEditor,
    Tiptap,
    CropEditor,
    LanyardPanel,
    RoundItemPanel,
  },
  props: {
    src: {
      type: String,
      default: '',
    },
    imageAnnotation: {
      type: Array,
      default: () => [],
    },
    imageDecoration: {
      type: Array,
      default: () => [],
    },
    util: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tipTapContentShapeId: null,
      activeInteractionMode: 'auto',

      // Our editor custom configuration options
      editorDefaults: {
        imageReader: createDefaultImageReader(),

        imageWriter: createDefaultImageWriter(),
        stickerEnableSelectImage: false,

        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        annotateActiveTool: 'rectangle',
        // NOTE: To enable the shapes from the annotation array to be selectable try setting this to something different
        utils: ['decorate'],
        decorateTools: [],
        decorateActiveTool: 'move',
        enableToolbar: true,
        enableButtonExport: false,
        enableButtonRevert: false,
        enableNavigateHistory: false,

        // The icons and labels to use in the user interface (required)
        locale: {
          ...locale_en_gb,
          ...plugin_decorate_locale_en_gb,
          ...markup_editor_locale_en_gb,
        },

        willRenderShapeControls: (controls, _selectedShapeId) => {
          const alphaNode = findNode('alpha', controls);
          const shape = this.shapes.find(
            (shape) => shape.id === _selectedShapeId
          );

          const isTextShape = shape?.w2pText !== undefined;
          const isRoundLogo = shape?.isRoundLogo;

          // Remove ajust opacity control, because we do not need it
          if (alphaNode && Array.isArray(alphaNode[3])) {
            let ajustOpacityIndex = alphaNode[3].findIndex(
              (tool) => tool[1] === 'adjust-opacity'
            );
            alphaNode[3][ajustOpacityIndex] = false;
          }

          const betaNode = findNode('beta', controls);

          if (betaNode && Array.isArray(betaNode[3])) {
            let flipHorizontalIndex = betaNode[3].findIndex(
              (tool) => tool[1] === 'flip-horizontal'
            );
            betaNode[3][flipHorizontalIndex] = false;
            let toFrontIndex = betaNode[3].findIndex(
              (tool) => tool[1] === 'to-front'
            );
            betaNode[3][toFrontIndex] = false;

            let duplicateIndex = betaNode[3].findIndex(
              (tool) => tool[1] === 'duplicate'
            );
            const duplicateNode = betaNode[3][duplicateIndex];
            duplicateNode[2].label = this.$t('tools.custom.duplicate');

            let removeIndex = betaNode[3].findIndex(
              (tool) => tool[1] === 'remove'
            );

            const removeNode = betaNode[3][removeIndex];
            removeNode[2].label = this.$t('tools.custom.remove');
          }

          //add 'spread all to right edge' only if area is rectangle

          // appendNode(
          //   createNode('Button', 'custom-spread', {
          //     class: 'custom-shape-control',
          //     label: 'spread all to right edge',
          //     hideLabel: true,
          //     icon: strFitToWidth,
          //     onclick: () => {
          //       this.executeSpread();
          //     },
          //   }),
          //   betaNode
          // );

          this.arraymove(betaNode[3], betaNode[3].length - 1, 3);

          // add 'duplicate to oposite side' only if area is rectangle

          // appendNode(
          //   createNode('Button', 'duplicate-to-oposite', {
          //     class: 'custom-shape-control',
          //     label: 'duplicate to oposite side',
          //     hideLabel: true,
          //     icon: strReplicate,
          //     onclick: () => {
          //       this.executeDuplicateToOposite();
          //     },
          //   }),
          //   betaNode
          // );

          this.arraymove(betaNode[3], betaNode[3].length - 1, 3);

          const nodesArray = this.determineShapeActions(
            shape,
            isTextShape,
            isRoundLogo
          );

          const nodes = nodesArray.map((node) => {
            return createNode('Button', node.id, {
              label: this.$t(`tools.custom.${node.label}`),
              class: node.class,
              hideLabel: node.hideLabel,
              icon: node.icon,
              onclick: () =>
                this.applyShapeControl.apply(null, [node, _selectedShapeId]),
            });
          });

          nodes.forEach((node) => appendNode(node, betaNode));

          isTextShape &&
            appendNode(
              createNode('Button', 'custom-edit-text', {
                label: this.$t('pintura.toolbar--context.edit-text--custom'),
                onclick: () => {
                  /**
                   * Show custom text editor instance
                   */
                  this.$modal.show('tiptap-modal', { id: _selectedShapeId });
                },
              }),
              betaNode
            );
          const deltaNode = findNode('delta', controls);

          if (deltaNode && Array.isArray(deltaNode[3])) {
            let editTextIndex = deltaNode[3].findIndex(
              (tool) => tool[1] === 'edit-text'
            );
            deltaNode[3][editTextIndex] = false;
          }

          const gammaNode = findNode('gamma', controls);

          if (gammaNode && Array.isArray(gammaNode[3])) {
            let textLayoutIndex = gammaNode[3].findIndex(
              (tool) => tool[1] === 'text-layout'
            );
            gammaNode[3][textLayoutIndex] = false;
          }
          return controls;
        },
      },
      initializeCropEditor: false,
      isLanyardPanelOpen: false,
      isRoundItemPanelOpen: false,
    };
  },

  computed: {
    ...mapStores(useShapeStore),
    ...mapState(useProductStore, [
      'unavailablePrintPositions',
      'activePosition',
      'rotation',
      'selectedPrintPosition',
      'selectedVariant',
      'data',
      'isLaserConverter',
      'isDebossingTechnique',
      'getDebossingColor',
      'printPositions',
    ]),
    ...mapState(useShapeStore, ['shapes', 'activeShapeId', 'panelPosition']),
    ...mapState(useHistoryStore, ['isUndoPossible', 'isRedoPossible']),
    ...mapState(useMirrorStore, ['currentMirrorSettings']),

    tipTapAttributes() {
      const text = this.shapes.find(
        (shape) => shape.id === this.tipTapContentShapeId
      );

      return text?.w2pText || undefined;
    },

    customTools() {
      let tools = createCustomTools().map((t) => {
        return {
          ...t,
          api: {
            onClick: () => {
              return this.applyToolControl(t.control, t.args);
            },
          },
        };
      });

      if (!this.currentMirrorSettings) {
        tools = tools.filter((x) => x.id !== 'lanyard');
      }
      if (this.activePosition.category !== Category.RoundItem) {
        tools = tools.filter((x) => x.id !== 'roundItems');
      }

      return tools;
    },
  },

  watch: {
    unavailablePrintPositions(n) {
      if (!this.pintura) return;
      if (n.length === 0) {
        this.pintura.editor.status = [this.$t('pintura.status'), undefined];
      } else {
        this.pintura.editor.status = undefined;
      }
    },
  },

  created() {
    this.$nextTick(() => {
      window.pintura = this.$refs['pinturaEditor'];
      this.pintura = window.pintura;
    });
    /**
     * Clean up editor instance to prevent memory leaks
     */
    this.$once('hook:beforeDestroy', () => {
      window.pintura = undefined;
      this.pintura = undefined;
    });
  },

  methods: {
    log: window.console.log,
    ...mapActions(useProductStore, [
      'getVariantLaserColor',
      'drawPoints',
      'polygonDetails',
      'updateProductStateFromSnapshot',
    ]),
    ...mapActions(useHistoryStore, ['undo', 'redo']),
    ...mapActions(useShapeStore, [
      'updateShapeStateFromSnapshot',
      'textToImage',
    ]),
    ...mapActions(useNotificationStore, [
      'updateNotificationStateFromSnapshot',
    ]),
    ...mapActions(useMirrorStore, [
      'updateMirrorStateFromSnapshot',
      'updateMirrorShape',
    ]),

    undoHandler() {
      const stateSnapshot = this.undo();

      if (stateSnapshot) {
        if (stateSnapshot.productState !== undefined) {
          this.updateProductStateFromSnapshot(
            cloneDeep(stateSnapshot.productState)
          );
        }

        if (stateSnapshot.shapeState !== undefined) {
          this.updateShapeStateFromSnapshot(
            cloneDeep(stateSnapshot.shapeState)
          );
        }

        if (stateSnapshot.notificationState !== undefined) {
          this.updateNotificationStateFromSnapshot(
            cloneDeep(stateSnapshot.notificationState)
          );
        }

        if (stateSnapshot.mirrorState !== undefined) {
          this.updateMirrorStateFromSnapshot(
            cloneDeep(stateSnapshot.mirrorState)
          );
        }

        const eventPayload = generateEventPayload();
        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },

    redoHandler() {
      const stateSnapshot = this.redo();

      if (stateSnapshot) {
        if (stateSnapshot.productState !== undefined) {
          this.updateProductStateFromSnapshot(
            cloneDeep(stateSnapshot.productState)
          );
        }

        if (stateSnapshot.shapeState !== undefined) {
          this.updateShapeStateFromSnapshot(
            cloneDeep(stateSnapshot.shapeState)
          );
        }

        if (stateSnapshot.notificationState !== undefined) {
          this.updateNotificationStateFromSnapshot(
            cloneDeep(stateSnapshot.notificationState)
          );
        }

        if (stateSnapshot.mirrorState !== undefined) {
          this.updateMirrorStateFromSnapshot(
            cloneDeep(stateSnapshot.mirrorState)
          );
        }

        const eventPayload = generateEventPayload();
        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },

    isRoundItemDisabled() {
      return !this.activeShapeId;
    },
    onLoad(event) {
      this.$emit('load', event);
    },

    arraymove(arr, fromIndex, toIndex) {
      var element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
    },

    executeDuplicateToOposite() {
      const shapesStore = useShapeStore();
      const productStore = useProductStore();

      const printPositionArea = productStore.drawPoints(
        productStore.selectedPrintPosition.id
      );

      const selectedShape = shapesStore.shapes?.find(
        (a) => a.id === shapesStore.activeShapeId
      );

      var newX = 0;

      const distancePositionStart = selectedShape.x - printPositionArea.x;
      if (
        printPositionArea.w / 2 >
        distancePositionStart + selectedShape.width / 2
      ) {
        newX =
          selectedShape.x +
          printPositionArea.w -
          distancePositionStart * 2 -
          selectedShape.width;
      } else {
        newX =
          printPositionArea.x +
          printPositionArea.w -
          distancePositionStart -
          selectedShape.width;
      }

      const id = uuidv4();

      shapesStore.addArtworkPayload(
        {
          ...selectedShape,
          id: id,
          personalizationArtworkId: id,
          x: newX,
        },
        productStore.selectedPrintPosition.id,
        productStore.selectedPrintPosition.techniqueId
      );
    },

    determineShapeActions(shape, isTextShape, isRoundLogo) {
      if (
        this.activePosition.printArea.shapeType === 'Polygon' &&
        !isTextShape &&
        !isRoundLogo
      ) {
        return contextNodes.filter((node) => node.id === 'crop-control');
      }

      if (
        this.activePosition.printArea.shapeType === 'Polygon' &&
        (isTextShape || isRoundLogo)
      ) {
        return [];
      }

      if (shape && shape.isInArea) {
        return isTextShape || isRoundLogo
          ? contextNodes.filter((x) => x.id !== 'crop-control')
          : contextNodes;
      } else {
        return contextNodes.filter(
          (node) =>
            node.id === 'align-v-center-control' ||
            node.id === 'align-h-center-control' ||
            (!isTextShape && !isRoundLogo && node.id === 'crop-control')
        );
      }
    },

    beforeOpenTipTapModal(e) {
      this.tipTapContentShapeId = e.params.id;
    },

    isNewSmallDetailsTextCheckNeeded(index, attributes) {
      const text = this.shapes[index];

      if (
        'fontFamily' in attributes &&
        text.fontFamily !== attributes.fontFamily
      ) {
        return true;
      }

      if ('fontSize' in attributes && text.fontSize !== attributes.fontSize) {
        return true;
      }

      if (
        'fontWeight' in attributes &&
        text.fontWeight !== attributes.fontWeight
      ) {
        return true;
      }

      if (
        'fontStyle' in attributes &&
        text.fontStyle !== attributes.fontStyle
      ) {
        return true;
      }

      if (
        'textAlign' in attributes &&
        text.textAlign !== attributes.textAlign
      ) {
        return true;
      }

      if ('text' in attributes && text.text !== attributes.text) {
        return true;
      }

      return false;
    },

    updateCustomEditorContentAttributes({ id, ...restOfAttrs }) {
      this.textToImage(
        restOfAttrs.fontFamily,
        restOfAttrs.fontSizePT,
        restOfAttrs.fontWeight,
        restOfAttrs.fontStyle,
        restOfAttrs.text,
        restOfAttrs.textAlign ?? '',
        restOfAttrs.colorName,
        restOfAttrs.textHtml,
        restOfAttrs.color,
        restOfAttrs.repositionText,
        id
      );
    },

    applyToolControl(cmd, args) {
      const artworkSpaceConsumed = 90; // value in percents
      if (cmd === 'apply-text-control') {
        const template = cloneDeep(args.template);

        template.id = uuidv4();
        template.isSelected = true;

        if (this.isLaserConverter()) {
          const configObj = this.getVariantLaserColor(
            this.selectedPrintPosition.id,
            this.selectedPrintPosition.techniqueId,
            this.selectedVariant.sku
          );

          const { red: r, green: g, blue: b } = configObj.laserColor;

          template.w2pText = {
            ...template.w2pText,
            color: [r, g, b],
          };
        }

        if (this.isDebossingTechnique()) {
          const debossingColor = this.getDebossingColor;
          if (debossingColor) {
            template.w2pText = {
              ...template.w2pText,
              color: debossingColor,
            };
          }
        }

        if (this.activePosition.printArea.shapeType === 'Rectangle') {
          template.width = (this.drawPoints().w * artworkSpaceConsumed) / 100;
          // do not use whole height from area, because for some large ractangle
          // areas it is not necessary (-40)
          template.height =
            (this.drawPoints().h * (artworkSpaceConsumed - 40)) / 100;
          template.x =
            this.drawPoints().x + this.drawPoints().w / 2 - template.width / 2;
          template.y =
            this.drawPoints().y + this.drawPoints().h / 2 - template.height / 2;
          template.rotation = this.rotation;
        }

        if (this.activePosition.printArea.shapeType === 'Ellipse') {
          template.width = this.drawPoints().rx;
          template.height = this.drawPoints().ry;
          template.x = this.drawPoints().x - template.width / 2;
          template.y = this.drawPoints().y - template.height / 2;
          template.rotation = this.rotation;
        }

        if (this.activePosition.printArea.shapeType === 'Polygon') {
          template.width =
            (this.polygonDetails().minDistance * (artworkSpaceConsumed - 20)) /
            100;
          template.height =
            (this.polygonDetails().minDistance * (artworkSpaceConsumed - 20)) /
            100;
          template.x = this.polygonDetails().x - template.width / 2;
          template.y = this.polygonDetails().y - template.height / 2;
          template.rotation = 0;
        }

        this.$emit('add-to:image-decoration', template);
        this.$modal.show('tiptap-modal', { id: this.activeShapeId });
      }

      if (cmd === 'apply-modal-control') {
        this.$emit('modal:open', {});
      }

      if (cmd === 'apply-pan-mode') {
        this.activeInteractionMode = 'pan';
        this.pintura.editor.markupEditorInteractionMode = 'pan';
      }

      if (cmd === 'apply-select-mode') {
        this.activeInteractionMode = 'auto';
        this.pintura.editor.markupEditorInteractionMode = 'auto';
      }

      if (cmd === 'apply-history-back-control') {
        this.undoHandler();
      }

      if (cmd === 'apply-history-forward-control') {
        this.redoHandler();
      }

      if (cmd === 'lanyard-options') {
        this.isLanyardPanelOpen = !this.isLanyardPanelOpen;
      }
      if (cmd === 'round-item-options') {
        this.isRoundItemPanelOpen = !this.isRoundItemPanelOpen;
      }
    },

    applyShapeControl(node, selectedShapeId) {
      const activeShape = this.pintura.editor.imageDecoration.find(
        (shape) => shape.id === selectedShapeId
      );

      if (!activeShape) return;

      const productStore = useProductStore();

      const printableAreaShapeType =
        productStore.activePosition.printArea?.shapeType || '';

      const printableArea = productStore.drawPoints();
      const printableAreaRotation = productStore.rotation;

      if (!printableArea) return;

      /**
       * Value can be either percentage or pixel, depending on how the shape
       * was initialized
       */
      const { width, height } = activeShape;

      /**
       * Normalize values for width & height
       * NOTE: We use hardcoded dimensions until we find the proper solution
       * once the backend logic for product area points gets adjusted
       */
      const actualWidth = isPercentageValue(activeShape.width)
        ? (Number.parseFloat(activeShape.width) / 100) *
          (this.activePosition.originalImageWidthPx || 227)
        : width;
      const actualHeight = isPercentageValue(activeShape.height)
        ? (Number.parseFloat(activeShape.height) / 100) *
          (this.activePosition.originalImageHeightPx || 402)
        : height;

      /**
       * Branch logic based on node.id
       * Calculate right x and y values for different kind of alignment controls
       * NOTE Include correction factor for now, until we see what's happening!
       */
      const CORRECTION_FACTOR = 0;
      const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
        productStore.activePosition;

      const webImageWidthPx =
        originalImageWidthPx &&
        originalImageWebDpi &&
        originalImageDpi &&
        (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
      const rectLineWidth = (lineWidth * webImageWidthPx) / 100;

      if (node.id === 'align-left-control') {
        if (printableAreaShapeType === 'Rectangle') {
          if (printableAreaRotation === 0 && activeShape.rotation === 0) {
            activeShape.x = this.drawPoints().x + rectLineWidth;
          } else {
            activeShape.x = alignLeftControl(
              rectLineWidth,
              activeShape,
              printableAreaShapeType
            );
          }
        }
        if (printableAreaShapeType === 'Ellipse') {
          activeShape.x = alignLeftControl(
            rectLineWidth,
            activeShape,
            printableAreaShapeType
          );
        }
        this.updateMirrorShape(activeShape);
      }

      if (node.id === 'align-right-control') {
        if (printableAreaShapeType === 'Rectangle') {
          if (printableAreaRotation === 0 && activeShape.rotation === 0) {
            activeShape.x =
              this.drawPoints().x +
              this.drawPoints().w -
              actualWidth -
              rectLineWidth;
          } else {
            activeShape.x = alignRightControl(
              rectLineWidth,
              activeShape,
              printableAreaShapeType,
              actualWidth
            );
          }
        }
        if (printableAreaShapeType === 'Ellipse') {
          activeShape.x = alignRightControl(
            rectLineWidth,
            activeShape,
            printableAreaShapeType,
            actualWidth
          );
        }
        this.updateMirrorShape(activeShape);
      }

      if (node.id === 'align-top-control') {
        if (printableAreaShapeType === 'Rectangle') {
          if (printableAreaRotation === 0 && activeShape.rotation === 0) {
            activeShape.y = this.drawPoints().y + rectLineWidth;
          } else {
            activeShape.y = alignTopControl(
              rectLineWidth,
              activeShape,
              printableAreaShapeType
            );
          }
        }
        if (printableAreaShapeType === 'Ellipse') {
          activeShape.y = alignTopControl(
            rectLineWidth,
            activeShape,
            printableAreaShapeType
          );
        }

        this.updateMirrorShape(activeShape);
      }

      if (node.id === 'align-bottom-control') {
        if (printableAreaShapeType === 'Rectangle') {
          if (printableAreaRotation === 0 && activeShape.rotation === 0) {
            activeShape.y =
              this.drawPoints().y +
              this.drawPoints().h -
              actualHeight -
              rectLineWidth;
          } else {
            activeShape.y = alignBottomControl(
              rectLineWidth,
              activeShape,
              printableAreaShapeType,
              actualHeight
            );
          }
        }
        if (printableAreaShapeType === 'Ellipse') {
          activeShape.y = alignBottomControl(
            rectLineWidth,
            activeShape,
            printableAreaShapeType,
            actualHeight
          );
        }
        this.updateMirrorShape(activeShape);
      }

      if (node.id === 'align-v-center-control') {
        if (printableAreaShapeType === 'Rectangle') {
          activeShape.y =
            this.drawPoints().y + this.drawPoints().h / 2 - actualHeight / 2;
        }
        if (printableAreaShapeType === 'Ellipse') {
          activeShape.y = this.drawPoints().y - actualHeight / 2;
        }
        if (printableAreaShapeType === 'Polygon') {
          activeShape.y = this.polygonDetails().y - actualHeight / 2;
        }
        this.updateMirrorShape(activeShape);
      }

      if (node.id === 'align-h-center-control') {
        if (printableAreaShapeType === 'Rectangle') {
          activeShape.x =
            this.drawPoints().x + this.drawPoints().w / 2 - actualWidth / 2;
        }
        if (printableAreaShapeType === 'Ellipse') {
          activeShape.x = this.drawPoints().x - actualWidth / 2;
        }
        if (printableAreaShapeType === 'Polygon') {
          activeShape.x = this.polygonDetails().x - actualWidth / 2;
        }

        this.updateMirrorShape(activeShape);
      }

      if (node.id === 'crop-control') {
        this.initializeCropEditor = true;
      }

      this.$emit(
        'update:image-decoration',
        this.pintura.editor.imageDecoration,
        node.id
      );
      this.$emit('app:detect-collision');

      if (node.id !== 'crop-control' && node.id !== 'remove') {
        const historyStore = useHistoryStore();
        const snapshot = createStateSnapshot();
        historyStore.recordHistory(snapshot);
      }
    },

    /**
     * Generic event handler for all types of public events
     * that editor might be sending (rule them all here!)
     */
    handleEvent(type, detail) {
      if (type === 'selectshape') {
        this.$emit(
          'update:image-decoration',
          this.pintura.editor.imageDecoration,
          type
        );
      }
      /**
       * Fires on shape clone (maybe some other events as well!?)
       */
      if (type === 'addshape') {
        const newId = uuidv4();
        detail.id = newId;

        if (detail.kind === 'Logo') {
          const originalShape = this.shapes.find(
            (x) => x.id === detail.personalizationArtworkId
          );

          if (originalShape && originalShape.kind === 'Logo') {
            this.$emit('add-to:image-decoration', {
              ...detail,
              id: newId,
              personalizationArtworkId: newId,
              pngFileWithoutConverters: originalShape.pngFileWithoutConverters,
              pngFile: originalShape.pngFile,
              smallDetailsResult: originalShape.smallDetailsResult,
              spotColorsInOriginalFile: originalShape.spotColorsInOriginalFile,
              pmsColorsInPngFile: originalShape.pmsColorsInPngFile,
              variantArtworkImages: originalShape.variantArtworkImages,
            });
          }
        }

        if (detail.kind === 'Text') {
          const originalText = this.shapes.find(
            (x) =>
              x.w2pText !== undefined &&
              x.w2pText.text === detail.w2pText.text &&
              x.id === this.activeShapeId
          );
          if (originalText) {
            this.$emit('add-to:image-decoration', {
              ...cloneDeep(originalText),
              x: detail.x,
              y: detail.y,
              id: newId,
            });
          }
        }
      }
    },

    beforeSelectShape(current, target) {
      this.$emit('beforeselect-shape', target);
      return true;
    },

    beforeUpdateShape(shape, props, ctx) {
      if (
        DoSmallDetailsPart &&
        (props.width !== undefined || props.height !== undefined)
      ) {
        const shapeIdx = this.shapes.findIndex((x) => x.id === shape.id);
        const shapeState = this.shapes[shapeIdx];

        if (shapeState.smallDetailsResult) {
          const smallDetailsResult = {
            ...shapeState.smallDetailsResult,
            isCheckValid: false,
          };
          shapeState.smallDetailsResult = smallDetailsResult;
        }
      }

      if (props.width !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.width = props.width < 1 ? 1 : props.width;
        props.height = props.width === 1 ? shape.height : props.height;
      }

      if (props.height !== undefined) {
        // min 1px
        // prevent logo from disappearing
        // keep aspect ratio
        props.height = props.height < 1 ? 1 : props.height;
        props.width = props.height === 1 ? shape.width : props.width;
      }

      const limit = limitToContextWithCorrectionFactor(
        ctx,
        undefined, // use default offset
        props,
        shape
      );

      /**app
       * *** SPECIAL: In case we get a rotation property inside props -
       * Here x and y aren't set at all, so we need to return the orginal back
       * without any modification
       *
       * VERY IMPORTANT:
       * Bear in mind props will be of a different shape, depending on action
       * being performed
       */
      const rotation = ((shape.rotation * 180) / Math.PI) % 360;
      if ((rotation !== 0 && rotation !== 180) || props.rotation) {
        return props;
      }

      this.$emit('beforeupdate-shape', shape);

      /**
       * Otherwise apply limitation to x and y
       * cases like scaling and moving operations
       */
      return {
        ...props,
        x: limit.x,
        y: limit.y,
      };
    },

    beforeDeselectShape(current, target) {
      this.$emit('beforedeselect-shape', { current, target });
      return true;
    },

    beforeRemoveShape(shape) {
      return true;
    },

    positionLogoInFirstPanel() {
      let calcArtworkWidth = 0;
      let calcArtworkHeight = 0;
      const CORRECTION_FACTOR = 0;

      let payload = this.shapes.find(
        (shape) => shape.id === this.activeShapeId
      );

      const currentPrintPosition = this.printPositions?.find((p) => {
        return p.id === this.activePosition.id;
      });

      const positionId = this.activePosition.id;

      const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
        this.activePosition;

      const webImageWidthPx =
        originalImageWidthPx &&
        originalImageWebDpi &&
        originalImageDpi &&
        (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;

      const rectLineWidth = (lineWidth * Number(webImageWidthPx)) / 100;

      if (payload && (payload.kind === 'Logo' || payload.kind === 'Text')) {
        if (
          currentPrintPosition &&
          currentPrintPosition.printArea?.shapeType === 'Rectangle'
        ) {
          const printPositionY1 =
            this.activePosition.printArea.productPrintAreaPoints.find(
              (point) => point.sequenceNo === 1
            ).distanceFromTop;
          const printPositionY2 =
            this.activePosition.printArea.productPrintAreaPoints.find(
              (point) => point.sequenceNo === 2
            ).distanceFromTop;

          const areaRect = {
            x: this.drawPoints(positionId).x + rectLineWidth / 2,
            y: this.drawPoints(positionId).y + rectLineWidth / 2,
            w: this.drawPoints(positionId).w - rectLineWidth,
            h: this.drawPoints(positionId).h - rectLineWidth,
          };
          const artworkRatio = payload.pngWidthPixels / payload.pngHeightPixels;
          const areaRatio = this.panelPosition.width / areaRect.h;

          // Artwork is more landscape than area - fit to width
          if (artworkRatio > areaRatio) {
            calcArtworkWidth = this.panelPosition.width - CORRECTION_FACTOR;
            calcArtworkHeight =
              (calcArtworkWidth / payload.pngWidthPixels) *
              payload.pngHeightPixels;
          }
          // Artwork is ,more portrait than area - fit to height
          else {
            calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
            calcArtworkWidth =
              (calcArtworkHeight / payload.pngHeightPixels) *
              payload.pngWidthPixels;
          }
          let gapH = areaRect.h - calcArtworkHeight;

          payload.x = this.panelPosition.x + lineWidth;

          payload.y = areaRect.y + areaRect.h / 2 - payload.height / 2;
        }

        const recalculatedHDPI = recalculateDPI(
          px2mm(
            calcArtworkWidth,
            Number(currentPrintPosition?.originalImageWebDpi)
          ),
          payload.originalHorizontalResolutionDPIs,
          payload.originalWidthMM
        );

        const recalculatedVDPI = recalculateDPI(
          px2mm(
            calcArtworkHeight,
            Number(currentPrintPosition?.originalImageWebDpi)
          ),
          payload.originalVerticalResolutionDPIs,
          payload.originalHeightMM
        );

        payload.pngHorizontalResolutionDPIs = Math.round(recalculatedHDPI);
        payload.pngVerticalResolutionDPIs = Math.round(recalculatedVDPI);
        payload.pngFileWithoutConverters = payload.pngFile;
      }

      this.$emit('update-shape', payload);
    },
  },
});
</script>
<style lang="scss">
@import '../../../node_modules/pintura/pintura.css';
.PinturaRoot .PinturaNavGroup:last-of-type {
  margin-top: 20px;
  margin-left: 45%;
}
.PinturaPanel {
  box-shadow: unset;
  border-radius: unset;
}

#toolbar--is_scoped {
  background-color: rgb(244, 244, 244);
}

.pintura-editor {
  --color-foreground: 0, 0, 0;
  /* --color-preview-outline: 255, 255, 255; */
  --color-background: 244, 244, 244;

  /**
 * Setting primary and secondary colors can be done with the
 * --color-primary and --color-secondary properties,
 * ...these properties take any CSS color value.
 * The --color-primary-text value is used for text rendered on elements that have
 * --color-primary as their background color.
 */
  /* --color-primary: #2990ff;
  --color-primary-dark: #1a80ec;
  --color-primary-text: #fff;
  --color-secondary: #03a9f4;
  --color-secondary-dark: #046bbf; */

  /* Will limit the max width of the editor modal. Defaults to none */
  /* --editor-max-width: 50em; */
  /* Will limit the max height of the editor modal. Defaults to none */
  /* --editor-max-height: 40em; */

  /* To add some additional padding to the top and/or bottom of the editor we can */
  /* use --editor-inset-top and --editor-inset-bottom */
  /* --editor-inset-top: 20px;

  --border-radius-round: 0em;
  --border-radius: 0em;
  --button-cursor: pointer; */

  /**
   * Font settings
   */
  --font-family: 'Roboto', sans-serif;
  --font-size: 16px;
}

.CustomTabList {
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  flex-direction: column;
  background: lightgrey;
  button {
    border: 1px solid grey;
    width: 4em;
    height: 4em;
  }
}

// .PinturaRoot button {
//   background-color: #393939;
//   color: white;
// }

/**
 * Makins overrides on a specific toolset
 */
.PinturaShapePresetsPalette {
  --color-foreground-10: #2990ff;

  button {
    background-color: #0f62fe;
    color: white;
  }

  .PinturaButton {
    padding: 0.6rem;
  }

  .PinturaButton:hover {
    box-shadow: inset 0 0 0 1px var(--color-foreground-10);
  }
}
</style>
<style lang="scss">
@import '../../styles/carbon-utils';
@import '../../styles/carbon-overrides.scss';
@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,700;1,400&display=swap');
#root {
  position: relative;
  height: 100%;

  #header {
    position: relative;
  }
}
.PinturaRoot {
  padding-top: 0;
  background: #f4f4f4 !important;
}
.PinturaRoot[data-env~='landscape'] > .PinturaNavTools {
  justify-content: left;
}
.CustomTabList {
  display: none;
  background: #f4f4f4;

  .PinturaButton {
    width: 3rem;
    height: 3rem;
    border: none;
    padding: 0.1rem;
    margin: 0;
  }
}
.PinturaRoot[data-env~='landscape'] > .PinturaNavMain {
  display: none;
}
.PinturaRoot .PinturaEditorOverlay {
  background-color: rgba(0, 0, 0, 0.6);
}
.custom-shape-control.PinturaButtonIconOnly {
  width: 2.3em;
  height: 2.3em;
  svg {
    fill: white;
  }
}
.custom-bring-to-front.PinturaButtonIconOnly {
  svg {
    width: 80%;
  }
}
</style>
<style lang="scss">
.pintura-sticker-editor {
  --editor-modal-overlay-opacity: 0.65;
  --editor-modal-border-radius: 1.5rem;
  --editor-max-width: 48%;
  --editor-max-height: 50%;
}
</style>
